import React, { useState, useMemo } from 'react';
import dayjs from 'dayjs';
import {
  Box,
  TextField,
  MenuItem,
  Stack,
  Typography,
  IconButton,
  Paper,
  Tooltip,
  Button,
  CircularProgress,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { darkTheme } from '../../../../../shared/context/ThemeContext';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { DurationSelector } from '../../../../../shared/components';
import type { DateRange } from '../../../../../shared/components/DurationSelector/types';
import { useDepartments, useShiftPatterns } from '../../../../../shared/components/Filters/hooks';
import { ChartBlockView } from './ChartBlockView';
import { SlideBlockView } from './SlideBlockView';
import { METRIC_OPTIONS, CHART_KIND_OPTIONS, GRANULARITY_OPTIONS, WINDOW_OPTIONS } from '../utils/metricOptions';
import type {
  ReportBlock,
  ChartBlock,
  HeadingBlock,
  NarrativeBlock,
  ColumnsBlock,
  ComparisonBlock,
  ImageBlock,
  SegmentComparisonBlock,
  SegmentEntry,
  CanvasSlideBlock,
} from '../api/types';
// Lazily imported to break the BlockEditor <-> CanvasSlideEditor import cycle:
// the canvas editor renders BlockEditor for the blocks inside a slide. It only
// renders inside a modal, so deferring the chunk until it opens costs nothing.
const CanvasSlideEditor = React.lazy(() =>
  import('./CanvasSlideEditor').then((m) => ({ default: m.CanvasSlideEditor })),
);

const SEGMENT_PALETTE = ['#2196f3', '#4caf50', '#ff9800', '#673ab7', '#e91e63', '#00bcd4'];

/** Block types produced by the generator that carry an editable title. */
/** Block types that carry a ReportFilters object with start_date/end_date. */
const FILTER_BEARING_TYPES = new Set([
  'execSummary',
  'breakdown',
  'kpiCards',
  'aiPanel',
  'correlation',
  'scatter',
  'weatherImpact',
  'comparison',
  'columns',
  'segmentComparison',
]);

/**
 * Converts ReportFilters start/end dates to a DateRange tuple for DurationSelector,
 * and converts back on change.
 */
const FiltersDatePicker: React.FC<{
  filters: import('../api/types').ReportFilters;
  onChange: (f: import('../api/types').ReportFilters) => void;
}> = ({ filters, onChange }) => {
  const value = useMemo<DateRange>(
    () => [filters.start_date ? dayjs(filters.start_date) : null, filters.end_date ? dayjs(filters.end_date) : null],
    [filters.start_date, filters.end_date],
  );

  return (
    <DurationSelector
      value={value}
      onChange={([start, end]) =>
        onChange({
          ...filters,
          start_date: start?.format('YYYY-MM-DD') ?? undefined,
          end_date: end?.format('YYYY-MM-DD') ?? undefined,
        })
      }
      buttonVariant='outlined'
      buttonLabel={
        value[0] && value[1]
          ? `${value[0].format('D MMM YY')} - ${value[1].format('D MMM YY')}`
          : 'Set reporting period'
      }
    />
  );
};

const TITLED_GENERATED = new Set([
  'cover',
  'execSummary',
  'breakdown',
  'kpiCards',
  'aiPanel',
  'actionPlan',
  'correlation',
  'scatter',
  'weatherImpact',
  'comparison',
  'segmentComparison',
]);

/** Generated block types that carry an editable key message under the data. */
const KEY_MESSAGE_TYPES = new Set(['execSummary', 'breakdown']);

/**
 * Metrics the comparison endpoint supports (used by breakdown / scatter /
 * correlation blocks). Ids match comparison.ts's SUPPORTED_METRICS.
 */
const COMPARISON_METRIC_OPTIONS: { value: string; label: string }[] = [
  { value: 'sleep_score', label: 'Sleep performance' },
  { value: 'recovery_score', label: 'Recovery' },
  { value: 'strain', label: 'Strain' },
  { value: 'resting_heart_rate', label: 'Resting heart rate' },
  { value: 'hrv', label: 'HRV' },
  { value: 'overall_stress', label: 'Overall stress' },
  { value: 'sleep_duration', label: 'Sleep duration' },
  { value: 'respiratory_rate', label: 'Respiratory rate' },
  { value: 'high_sleep_stress', label: 'High sleep stress' },
];

/** Segment dimensions a breakdown/correlation/scatter can group by. */
const DIMENSION_OPTIONS: { value: string; label: string }[] = [
  { value: 'gender', label: 'Gender' },
  { value: 'age_group', label: 'Age group' },
  { value: 'department', label: 'Department / role' },
  { value: 'shift_pattern', label: 'Shift pattern' },
];

/** UK weather regions for the weather impact block. */
const UK_REGIONS: { value: string; label: string }[] = [
  { value: 'North West', label: 'North West' },
  { value: 'North East', label: 'North East' },
  { value: 'Yorkshire', label: 'Yorkshire' },
  { value: 'East Midlands', label: 'East Midlands' },
  { value: 'West Midlands', label: 'West Midlands' },
  { value: 'East of England', label: 'East of England' },
  { value: 'London', label: 'London' },
  { value: 'South East', label: 'South East' },
  { value: 'South West', label: 'South West' },
  { value: 'Wales', label: 'Wales' },
  { value: 'Scotland', label: 'Scotland' },
  { value: 'Northern Ireland', label: 'Northern Ireland' },
];

/**
 * Wrapper component that owns the `editorOpen` state for the canvas slide
 * editor - avoids calling useState() inside a switch/case.
 */
const CanvasSlideBlockEditorRow: React.FC<{
  block: CanvasSlideBlock;
  clientId: string | null;
  onChange: (b: ReportBlock) => void;
}> = ({ block, clientId, onChange }) => {
  const [editorOpen, setEditorOpen] = useState(false);
  return (
    <Stack spacing={1.5}>
      <Stack direction='row' justifyContent='space-between' alignItems='center'>
        <Typography variant='body2' color='text.secondary'>
          {block.elements.length} element{block.elements.length !== 1 ? 's' : ''} on canvas
        </Typography>
        <Button variant='contained' size='small' onClick={() => setEditorOpen(true)}>
          Edit canvas
        </Button>
      </Stack>
      {block.elements.length > 0 && (
        <ThemeProvider theme={darkTheme}>
          <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 1, bgcolor: '#0B0F14' }}>
            <SlideBlockView block={block} clientId={null} />
          </Box>
        </ThemeProvider>
      )}
      {/* Only mount the lazy editor once it is opened, so the chunk loads on demand. */}
      {editorOpen && (
        <React.Suspense fallback={<CircularProgress size={20} />}>
          <CanvasSlideEditor
            block={block}
            clientId={clientId}
            open={editorOpen}
            onClose={() => setEditorOpen(false)}
            onChange={(next) => onChange(next as ReportBlock)}
          />
        </React.Suspense>
      )}
    </Stack>
  );
};

interface BlockEditorProps {
  block: ReportBlock;
  clientId: string | null;
  onChange: (block: ReportBlock) => void;
  onRemove: () => void;
  /** Drag handle props from dnd-kit's useSortable listeners/attributes. */
  dragHandleProps?: React.HTMLAttributes<HTMLElement>;
  /**
   * Generates Director-targeted narrative from the whole report (server reads
   * the saved layout). Provided only for narrative blocks; returns the text to
   * drop into this block. Undefined when generation isn't available.
   */
  onGenerateNarrative?: () => Promise<string>;
  /**
   * Generates an AI action plan (list of steps) for the whole report. Provided
   * only for action-plan blocks; returns the items to store on the block.
   */
  onGenerateActionPlan?: () => Promise<string[]>;
  /** When true (nested inside a columns slot): hides drag handle + date picker. */
  nested?: boolean;
}

/**
 * Upload/URL controls for an image block. This lives in its own component
 * because it owns hook state: inlining it in BlockEditor's `switch (block.type)`
 * meant the hooks only ran for image blocks, so changing a block's type shifted
 * the hook order and broke the rules of hooks.
 */
const ImageBlockEditor: React.FC<{
  img: ImageBlock;
  onChange: (block: ReportBlock) => void;
}> = ({ img, onChange }) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = React.useState(false);
  const [uploadError, setUploadError] = React.useState<string | null>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const { reportingApi: rApi } = await import('../api/reporting');
      const { upload_url, file_url } = await rApi.createImageUploadUrl({
        filename: file.name,
        content_type: file.type,
      });
      const res = await fetch(upload_url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });
      if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`);
      onChange({ ...img, url: file_url });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems='flex-start'>
        <input
          ref={fileInputRef}
          type='file'
          accept='image/*'
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />
        <Button
          variant='outlined'
          size='small'
          disabled={uploading}
          startIcon={uploading ? <CircularProgress size={14} /> : undefined}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading ? 'Uploading…' : 'Upload image'}
        </Button>
        <Typography variant='caption' color='text.secondary' sx={{ pt: 0.75 }}>
          or paste a URL below
        </Typography>
      </Stack>
      {uploadError && (
        <Typography variant='caption' color='error'>
          {uploadError}
        </Typography>
      )}
      <TextField
        fullWidth
        size='small'
        label='Image URL'
        value={img.url}
        onChange={(e) => onChange({ ...img, url: e.target.value })}
      />
      <TextField
        fullWidth
        size='small'
        label='Caption (optional)'
        value={img.caption ?? ''}
        onChange={(e) => onChange({ ...img, caption: e.target.value })}
      />
      <TextField
        select
        size='small'
        label='Fit'
        value={img.fit ?? 'contain'}
        onChange={(e) => onChange({ ...img, fit: e.target.value as ImageBlock['fit'] })}
        sx={{ width: 160 }}
      >
        <MenuItem value='contain'>Contain</MenuItem>
        <MenuItem value='cover'>Cover</MenuItem>
      </TextField>
      {img.url && (
        <Box
          component='img'
          src={img.url}
          alt={img.caption ?? ''}
          sx={{
            maxWidth: '100%',
            maxHeight: 200,
            objectFit: img.fit ?? 'contain',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        />
      )}
    </Stack>
  );
};

/**
 * Edit surface for one block. Chart blocks show config controls plus a live
 * preview; heading/narrative blocks are inline text; pageBreak is a divider.
 */
export const BlockEditor: React.FC<BlockEditorProps> = ({
  block,
  clientId,
  onChange,
  onRemove,
  dragHandleProps,
  onGenerateNarrative,
  onGenerateActionPlan,
  nested = false,
}) => {
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(false);
  const departmentsQuery = useDepartments();
  const shiftPatternsQuery = useShiftPatterns();
  const deptOptions: { id: string; name: string }[] = departmentsQuery.data?.data ?? [];
  const shiftOptions: { value: string; label: string }[] = shiftPatternsQuery.data?.data ?? [];
  const header = (
    <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1 }}>
      <Box {...dragHandleProps} sx={{ cursor: 'grab', display: 'flex', alignItems: 'center', color: 'text.disabled' }}>
        <DragIndicatorIcon fontSize='small' />
        <Typography variant='caption' sx={{ ml: 0.5, textTransform: 'capitalize' }}>
          {block.type === 'pageBreak' ? 'Page break' : block.type}
        </Typography>
      </Box>
      <Tooltip title='Remove block'>
        <IconButton size='small' onClick={onRemove}>
          <DeleteOutlineIcon fontSize='small' />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const renderBody = () => {
    switch (block.type) {
      case 'heading': {
        const b = block as HeadingBlock;
        return (
          <TextField
            fullWidth
            variant='standard'
            placeholder='Section heading'
            value={b.text}
            onChange={(e) => onChange({ ...b, text: e.target.value })}
            slotProps={{ input: { style: { fontSize: 24, fontWeight: 600 } } }}
          />
        );
      }
      case 'narrative': {
        const b = block as NarrativeBlock;
        const handleGenerate = async () => {
          if (!onGenerateNarrative) return;
          setGenerating(true);
          setGenError(false);
          try {
            const text = await onGenerateNarrative();
            onChange({ ...b, text });
          } catch {
            setGenError(true);
          } finally {
            setGenerating(false);
          }
        };
        return (
          <Stack spacing={1}>
            {onGenerateNarrative && (
              <Stack direction='row' alignItems='center' spacing={1}>
                <Tooltip title="Draft an executive narrative for a Director from this report's charts and copy">
                  <span>
                    <Button
                      size='small'
                      variant='outlined'
                      startIcon={generating ? <CircularProgress size={14} /> : <AutoAwesomeIcon fontSize='small' />}
                      onClick={handleGenerate}
                      disabled={generating}
                    >
                      Generate using AI
                    </Button>
                  </span>
                </Tooltip>
                <Typography variant='caption' color='text.secondary'>
                  Written for a Director
                </Typography>
              </Stack>
            )}
            {genError && (
              <Typography variant='caption' color='error'>
                Couldn't generate narrative. Save the report and try again.
              </Typography>
            )}
            <TextField
              fullWidth
              multiline
              minRows={2}
              placeholder='Narrative text…'
              value={b.text}
              onChange={(e) => onChange({ ...b, text: e.target.value })}
            />
            <TextField
              fullWidth
              multiline
              minRows={1}
              size='small'
              label='Narration script (optional - spoken in presentation mode)'
              value={b.narration ?? ''}
              onChange={(e) => onChange({ ...b, narration: e.target.value })}
            />
          </Stack>
        );
      }
      case 'chart': {
        const b = block as ChartBlock;
        return (
          <Stack spacing={2}>
            <TextField
              fullWidth
              size='small'
              label='Caption'
              value={b.title ?? ''}
              onChange={(e) => onChange({ ...b, title: e.target.value })}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                fullWidth
                size='small'
                label='Metric'
                value={b.metric}
                onChange={(e) => onChange({ ...b, metric: e.target.value })}
              >
                {METRIC_OPTIONS.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                size='small'
                label='Chart type'
                value={b.chartKind}
                onChange={(e) => onChange({ ...b, chartKind: e.target.value as ChartBlock['chartKind'] })}
              >
                {CHART_KIND_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                fullWidth
                size='small'
                label='Granularity'
                value={b.granularity}
                onChange={(e) => onChange({ ...b, granularity: e.target.value as ChartBlock['granularity'] })}
              >
                {GRANULARITY_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                size='small'
                label='Period'
                value={b.windowDays}
                onChange={(e) => onChange({ ...b, windowDays: Number(e.target.value) })}
              >
                {WINDOW_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
            <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 1 }}>
              <ChartBlockView block={b} clientId={clientId} />
            </Box>
          </Stack>
        );
      }
      case 'pageBreak':
        return (
          <Box
            sx={{
              borderTop: '2px dashed',
              borderColor: 'divider',
              textAlign: 'center',
              color: 'text.disabled',
              py: 0.5,
            }}
          >
            <Typography variant='caption'>- new presentation page / PDF page -</Typography>
          </Box>
        );
      case 'image':
        return <ImageBlockEditor img={block as ImageBlock} onChange={onChange} />;
      case 'columns': {
        const col = block as ColumnsBlock;

        const colId = () =>
          typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `col-${Date.now()}`;

        const inheritedFilters = (
          col.left && 'filters' in col.left
            ? (col.left as { filters: unknown }).filters
            : col.right && 'filters' in col.right
              ? (col.right as { filters: unknown }).filters
              : {}
        ) as import('../api/types').ReportFilters;

        const makeColBlock = (type: ReportBlock['type']): ReportBlock => {
          const id = colId();
          switch (type) {
            case 'chart':
              return {
                id,
                type: 'chart',
                title: '',
                chartKind: 'line',
                metric: 'recovery_score',
                granularity: 'daily',
                windowDays: 90,
              } as ChartBlock;
            case 'kpiCards':
              return {
                id,
                type: 'kpiCards',
                title: 'Key Metrics',
                metrics: ['recovery_score', 'sleep_score', 'strain', 'resting_heart_rate', 'hrv'],
                filters: inheritedFilters,
              };
            case 'breakdown':
              return {
                id,
                type: 'breakdown',
                title: 'Breakdown',
                dimension: 'department',
                metrics: ['sleep_score', 'recovery_score', 'strain', 'resting_heart_rate', 'hrv'],
                filters: inheritedFilters,
              };
            case 'comparison':
              return {
                id,
                type: 'comparison',
                title: 'Metric Comparison',
                metricA: 'recovery_score',
                metricB: 'sleep_performance',
                viewMode: 'trend',
                segmentBy: 'department',
                filters: inheritedFilters,
              };
            case 'segmentComparison':
              return {
                id,
                type: 'segmentComparison',
                title: 'Segment Comparison',
                metric: 'recovery_score',
                chartKind: 'line',
                granularity: 'daily',
                filters: inheritedFilters,
                segments: [
                  { label: 'Group A', filterOverride: {} },
                  { label: 'Group B', filterOverride: {} },
                ],
              };
            case 'weatherImpact':
              return {
                id,
                type: 'weatherImpact',
                title: 'Weather Impact',
                metric: 'recovery_score',
                filters: inheritedFilters,
              };
            case 'narrative':
              return { id, type: 'narrative', text: '' } as NarrativeBlock;
            default:
              return { id, type: 'narrative', text: '' } as NarrativeBlock;
          }
        };

        const COL_BLOCK_OPTIONS: { type: ReportBlock['type']; label: string }[] = [
          { type: 'chart', label: 'Chart' },
          { type: 'comparison', label: 'Metric comparison' },
          { type: 'segmentComparison', label: 'Segment comparison' },
          { type: 'kpiCards', label: 'KPI cards' },
          { type: 'breakdown', label: 'Breakdown' },
          { type: 'weatherImpact', label: 'Weather impact' },
          { type: 'narrative', label: 'Narrative text' },
        ];

        const renderColSlot = (side: 'left' | 'right', child: ReportBlock | null) => (
          <Box sx={{ flex: 1, minWidth: 0, border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 1 }}>
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 1 }}>
              {side === 'left' ? 'Left column' : 'Right column'}
            </Typography>
            {child ? (
              <BlockEditor
                block={child}
                clientId={clientId}
                onChange={(next) => onChange({ ...col, [side]: next } as ReportBlock)}
                onRemove={() => onChange({ ...col, [side]: null } as ReportBlock)}
                nested
              />
            ) : (
              <Stack spacing={0.5}>
                {COL_BLOCK_OPTIONS.map(({ type, label }) => (
                  <Button
                    key={type}
                    size='small'
                    variant='outlined'
                    onClick={() => onChange({ ...col, [side]: makeColBlock(type) } as ReportBlock)}
                  >
                    + {label}
                  </Button>
                ))}
              </Stack>
            )}
          </Box>
        );

        return (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            {renderColSlot('left', col.left)}
            {renderColSlot('right', col.right)}
          </Stack>
        );
      }
      case 'canvasSlide':
        return <CanvasSlideBlockEditorRow block={block as CanvasSlideBlock} clientId={clientId} onChange={onChange} />;
      default:
        break;
    }

    // Generated slide blocks (cover, execSummary, breakdown, kpiCards, aiPanel,
    // actionPlan, correlation, scatter, weatherImpact): editable title + optional
    // key message + a live preview of the rendered slide.
    if (TITLED_GENERATED.has(block.type)) {
      const b = block as ReportBlock & {
        title?: string;
        keyMessage?: string;
        filters?: import('../api/types').ReportFilters;
      };
      return (
        <Stack spacing={2}>
          {!nested && (
            <TextField
              fullWidth
              size='small'
              label='Slide title'
              value={b.title ?? ''}
              onChange={(e) => onChange({ ...b, title: e.target.value } as ReportBlock)}
            />
          )}
          {!nested && FILTER_BEARING_TYPES.has(block.type) && b.filters !== undefined && (
            <Stack direction='row' spacing={1} alignItems='center'>
              <Typography variant='caption' color='text.secondary' sx={{ flexShrink: 0 }}>
                Reporting period:
              </Typography>
              <FiltersDatePicker filters={b.filters} onChange={(f) => onChange({ ...b, filters: f } as ReportBlock)} />
            </Stack>
          )}
          {KEY_MESSAGE_TYPES.has(block.type) && (
            <TextField
              fullWidth
              multiline
              minRows={2}
              size='small'
              label='Key message (shown under the data)'
              value={b.keyMessage ?? ''}
              onChange={(e) => onChange({ ...b, keyMessage: e.target.value } as ReportBlock)}
            />
          )}
          {block.type === 'actionPlan' && (
            <Stack spacing={1}>
              {onGenerateActionPlan && (
                <Stack direction='row' alignItems='center' spacing={1}>
                  <Tooltip title='Draft a prioritised action plan for a Director from this report'>
                    <span>
                      <Button
                        size='small'
                        variant='outlined'
                        startIcon={generating ? <CircularProgress size={14} /> : <AutoAwesomeIcon fontSize='small' />}
                        onClick={async () => {
                          if (!onGenerateActionPlan) return;
                          setGenerating(true);
                          setGenError(false);
                          try {
                            const items = await onGenerateActionPlan();
                            onChange({ ...block, items });
                          } catch {
                            setGenError(true);
                          } finally {
                            setGenerating(false);
                          }
                        }}
                        disabled={generating}
                      >
                        Generate with AI
                      </Button>
                    </span>
                  </Tooltip>
                  <Typography variant='caption' color='text.secondary'>
                    Written for a Director
                  </Typography>
                </Stack>
              )}
              {genError && (
                <Typography variant='caption' color='error'>
                  Couldn't generate. Save the report and try again.
                </Typography>
              )}
              {block.items.length > 0 && (
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  size='small'
                  label='Action items (one per line)'
                  value={block.items.join('\n')}
                  onChange={(e) =>
                    onChange({
                      ...block,
                      items: e.target.value
                        .split('\n')
                        .map((l) => l.trim())
                        .filter(Boolean),
                    })
                  }
                />
              )}
            </Stack>
          )}
          {block.type === 'scatter' && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                fullWidth
                size='small'
                label='X metric'
                value={block.xMetric}
                onChange={(e) => onChange({ ...block, xMetric: e.target.value })}
              >
                {COMPARISON_METRIC_OPTIONS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                size='small'
                label='Y metric'
                value={block.yMetric}
                onChange={(e) => onChange({ ...block, yMetric: e.target.value })}
              >
                {COMPARISON_METRIC_OPTIONS.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                size='small'
                label='Group by'
                value={block.dimension}
                onChange={(e) =>
                  onChange({
                    ...block,
                    dimension: e.target.value as typeof block.dimension,
                  })
                }
              >
                {DIMENSION_OPTIONS.map((d) => (
                  <MenuItem key={d.value} value={d.value}>
                    {d.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          )}
          {block.type === 'breakdown' && (
            <TextField
              select
              fullWidth
              size='small'
              label='Group by'
              value={block.dimension}
              onChange={(e) =>
                onChange({
                  ...block,
                  dimension: e.target.value as typeof block.dimension,
                })
              }
            >
              {DIMENSION_OPTIONS.map((d) => (
                <MenuItem key={d.value} value={d.value}>
                  {d.label}
                </MenuItem>
              ))}
            </TextField>
          )}
          {block.type === 'correlation' && (
            <TextField
              select
              fullWidth
              size='small'
              label='Group by'
              value={block.dimension}
              onChange={(e) =>
                onChange({
                  ...block,
                  dimension: e.target.value as typeof block.dimension,
                })
              }
            >
              {DIMENSION_OPTIONS.map((d) => (
                <MenuItem key={d.value} value={d.value}>
                  {d.label}
                </MenuItem>
              ))}
            </TextField>
          )}
          {block.type === 'weatherImpact' && (
            <Stack spacing={2}>
              <TextField
                select
                fullWidth
                size='small'
                label='Overlay metric'
                value={block.metric ?? ''}
                onChange={(e) => onChange({ ...block, metric: e.target.value })}
              >
                <MenuItem value=''>
                  <em>None (weather only)</em>
                </MenuItem>
                {METRIC_OPTIONS.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                size='small'
                label='UK region (optional)'
                value={block.ukRegion ?? ''}
                onChange={(e) =>
                  onChange({
                    ...block,
                    ukRegion: e.target.value || undefined,
                  })
                }
              >
                <MenuItem value=''>
                  <em>All regions (from participant locations)</em>
                </MenuItem>
                {UK_REGIONS.map((r) => (
                  <MenuItem key={r.value} value={r.value}>
                    {r.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
          )}
          {block.type === 'segmentComparison' &&
            (() => {
              const sc = block as SegmentComparisonBlock;
              const updateSeg = (i: number, patch: Partial<SegmentEntry>) => {
                const next = sc.segments.map((s, idx) => (idx === i ? { ...s, ...patch } : s));
                onChange({ ...sc, segments: next } as ReportBlock);
              };
              const addSeg = () => {
                if (sc.segments.length >= 5) return;
                onChange({
                  ...sc,
                  segments: [...sc.segments, { label: `Segment ${sc.segments.length + 1}`, filterOverride: {} }],
                } as ReportBlock);
              };
              const removeSeg = (i: number) => {
                if (sc.segments.length <= 2) return;
                onChange({ ...sc, segments: sc.segments.filter((_, idx) => idx !== i) } as ReportBlock);
              };
              return (
                <Stack spacing={2}>
                  <Stack direction='row' spacing={2}>
                    <TextField
                      select
                      fullWidth
                      size='small'
                      label='Metric'
                      value={sc.metric}
                      onChange={(e) => onChange({ ...sc, metric: e.target.value } as ReportBlock)}
                    >
                      {COMPARISON_METRIC_OPTIONS.map((m) => (
                        <MenuItem key={m.value} value={m.value}>
                          {m.label}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      fullWidth
                      size='small'
                      label='Chart type'
                      value={sc.chartKind}
                      onChange={(e) =>
                        onChange({ ...sc, chartKind: e.target.value as 'line' | 'verticalBar' } as ReportBlock)
                      }
                    >
                      <MenuItem value='line'>Line</MenuItem>
                      <MenuItem value='verticalBar'>Bar</MenuItem>
                    </TextField>
                  </Stack>
                  <Stack spacing={1}>
                    {sc.segments.map((seg, i) => (
                      <Box key={i} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                        <Stack direction='row' justifyContent='space-between' alignItems='center' sx={{ mb: 1 }}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: SEGMENT_PALETTE[i % SEGMENT_PALETTE.length],
                            }}
                          />
                          {sc.segments.length > 2 && (
                            <Button
                              size='small'
                              color='error'
                              onClick={() => removeSeg(i)}
                              sx={{ minWidth: 0, px: 0.5 }}
                            >
                              ✕
                            </Button>
                          )}
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                          <TextField
                            fullWidth
                            size='small'
                            label='Label'
                            value={seg.label}
                            onChange={(e) => updateSeg(i, { label: e.target.value })}
                          />
                          <TextField
                            select
                            fullWidth
                            size='small'
                            label='Department'
                            value={seg.filterOverride.department?.[0] ?? ''}
                            onChange={(e) =>
                              updateSeg(i, {
                                filterOverride: {
                                  ...seg.filterOverride,
                                  department: e.target.value ? [e.target.value] : undefined,
                                },
                              })
                            }
                          >
                            <MenuItem value=''>
                              <em>All</em>
                            </MenuItem>
                            {deptOptions.map((d) => (
                              <MenuItem key={d.id} value={d.id}>
                                {d.name}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            select
                            fullWidth
                            size='small'
                            label='Gender'
                            value={seg.filterOverride.sex ?? ''}
                            onChange={(e) =>
                              updateSeg(i, {
                                filterOverride: { ...seg.filterOverride, sex: e.target.value || undefined },
                              })
                            }
                          >
                            <MenuItem value=''>
                              <em>All</em>
                            </MenuItem>
                            <MenuItem value='male'>Male</MenuItem>
                            <MenuItem value='female'>Female</MenuItem>
                          </TextField>
                          <TextField
                            select
                            fullWidth
                            size='small'
                            label='Shift'
                            value={seg.filterOverride.shift_pattern?.[0] ?? ''}
                            onChange={(e) =>
                              updateSeg(i, {
                                filterOverride: {
                                  ...seg.filterOverride,
                                  shift_pattern: e.target.value ? [e.target.value] : undefined,
                                },
                              })
                            }
                          >
                            <MenuItem value=''>
                              <em>All</em>
                            </MenuItem>
                            {shiftOptions.map((s) => (
                              <MenuItem key={s.value} value={s.value}>
                                {s.label}
                              </MenuItem>
                            ))}
                          </TextField>
                        </Stack>
                      </Box>
                    ))}
                    {sc.segments.length < 5 && (
                      <Button variant='outlined' size='small' onClick={addSeg}>
                        + Add segment
                      </Button>
                    )}
                  </Stack>
                </Stack>
              );
            })()}
          {block.type === 'comparison' && (
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select
                  fullWidth
                  size='small'
                  label='Metric A'
                  value={(block as ComparisonBlock).metricA}
                  onChange={(e) => onChange({ ...(block as ComparisonBlock), metricA: e.target.value })}
                >
                  {COMPARISON_METRIC_OPTIONS.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  fullWidth
                  size='small'
                  label='Metric B'
                  value={(block as ComparisonBlock).metricB}
                  onChange={(e) => onChange({ ...(block as ComparisonBlock), metricB: e.target.value })}
                >
                  {COMPARISON_METRIC_OPTIONS.map((m) => (
                    <MenuItem key={m.value} value={m.value}>
                      {m.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  select
                  fullWidth
                  size='small'
                  label='View'
                  value={(block as ComparisonBlock).viewMode}
                  onChange={(e) =>
                    onChange({
                      ...(block as ComparisonBlock),
                      viewMode: e.target.value as ComparisonBlock['viewMode'],
                    })
                  }
                >
                  <MenuItem value='trend'>Trend over time</MenuItem>
                  <MenuItem value='segmented'>Segmented bars</MenuItem>
                  <MenuItem value='scatter'>Correlation scatter</MenuItem>
                </TextField>
                <TextField
                  select
                  fullWidth
                  size='small'
                  label='Segment by'
                  value={(block as ComparisonBlock).segmentBy}
                  onChange={(e) =>
                    onChange({
                      ...(block as ComparisonBlock),
                      segmentBy: e.target.value as ComparisonBlock['segmentBy'],
                    })
                  }
                >
                  {[
                    { value: 'department', label: 'Department' },
                    { value: 'age_group', label: 'Age Group' },
                    { value: 'gender', label: 'Gender' },
                    { value: 'shift_pattern', label: 'Shift Pattern' },
                  ].map((d) => (
                    <MenuItem key={d.value} value={d.value}>
                      {d.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>
            </Stack>
          )}
          {!nested && (
            <ThemeProvider theme={darkTheme}>
              <Box
                sx={{
                  border: '1px dashed',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 1,
                  bgcolor: '#0B0F14',
                  color: '#fff',
                }}
              >
                <SlideBlockView block={block} clientId={clientId} />
              </Box>
            </ThemeProvider>
          )}
        </Stack>
      );
    }

    return null;
  };

  if (nested) {
    return <Box sx={{ width: '100%' }}>{renderBody()}</Box>;
  }

  return (
    <Paper variant='outlined' sx={{ p: 2, mb: 2 }}>
      {header}
      {renderBody()}
    </Paper>
  );
};
